import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

interface FieldInfoTooltipProps {
  content: string;
  className?: string;
}

interface LabelWithInfoProps {
  label: string;
  tooltip: string;
  required?: boolean;
  htmlFor?: string;
}

/**
 * Information icon that shows a tooltip on hover/focus, explaining why a field is collected.
 * Uses createPortal to render tooltip in document.body so it doesn't get cut off by scroll
 * containers or overflow:hidden parents.
 */
const FieldInfoTooltip: React.FC<FieldInfoTooltipProps> = ({
  content,
  className = '',
}) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible || !triggerRef.current) return;
    const updatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const tooltipHeight = 80;
      const gap = 8;
      setPosition({
        top: rect.top - tooltipHeight - gap,
        left: rect.left + rect.width / 2,
      });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [visible]);

  return (
    <>
      <span
        ref={triggerRef}
        className={`group relative inline-flex items-center ${className}`}
        tabIndex={0}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        onFocus={() => setVisible(true)}
        onBlur={() => setVisible(false)}
      >
        <Info
          className="h-4 w-4 text-gray-400 hover:text-herbal-green cursor-help shrink-0"
          aria-label="More information"
        />
      </span>
      {visible &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={tooltipRef}
            className="fixed z-[9999] px-3 py-2 max-w-[280px] min-w-[200px] text-sm text-gray-700 bg-white border border-gray-200 rounded-lg shadow-lg"
            style={{
              top: position.top,
              left: position.left,
              transform: 'translate(-50%, 0)',
            }}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
};

/**
 * Label with info icon and tooltip for form fields.
 */
export const LabelWithInfo: React.FC<LabelWithInfoProps> = ({
  label,
  tooltip,
  required = false,
  htmlFor,
}) => (
  <span className="inline-flex items-center gap-1.5">
    <label htmlFor={htmlFor} className="text-sm font-medium text-gray-700">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <FieldInfoTooltip content={tooltip} />
  </span>
);

export default FieldInfoTooltip;
